/**
 * Bayer ordered threshold maps, sizes 2, 4, 8, 16.
 *
 * Built recursively from the 2x2 base via M_{2n}[2y+i][2x+j] = 4*M_n[y][x] + B[i][j],
 * where B = [[0, 2], [3, 1]].
 *
 * Reference: https://surma.dev/things/ditherpunk/
 */

const BASE_QUAD = [0, 2, 3, 1];

function buildBayerMatrix(size: 2 | 4 | 8 | 16): Uint8Array {
  let n = 2;
  let m: number[] = [0, 2, 3, 1];

  while (n < size) {
    const newN = n * 2;
    const newM: number[] = new Array(newN * newN);
    for (let y = 0; y < newN; y++) {
      for (let x = 0; x < newN; x++) {
        const qx = x >= n ? 1 : 0;
        const qy = y >= n ? 1 : 0;
        const inX = x % n;
        const inY = y % n;
        const subVal = m[inY * n + inX] ?? 0;
        const baseVal = BASE_QUAD[qy * 2 + qx] ?? 0;
        newM[y * newN + x] = subVal * 4 + baseVal;
      }
    }
    m = newM;
    n = newN;
  }

  return new Uint8Array(m);
}

export const BAYER_2 = buildBayerMatrix(2);
export const BAYER_4 = buildBayerMatrix(4);
export const BAYER_8 = buildBayerMatrix(8);
export const BAYER_16 = buildBayerMatrix(16);

export const BAYER_MATRICES: Record<2 | 4 | 8 | 16, Uint8Array> = {
  2: BAYER_2,
  4: BAYER_4,
  8: BAYER_8,
  16: BAYER_16,
};
